import { useQuery } from "@tanstack/react-query";
import { adminFetch } from "./client";
import { queryKeys } from "./queryKeys";

export interface DashboardKPIs {
  active_users: number;
  active_deals: number;
  alerts_sent: number;
  deal_packs: number;
  /** SUPER_ADMIN only — the key is ABSENT for other roles rather than zeroed,
   *  so a missing figure cannot be mistaken for "no revenue" (MPPI-00057). */
  revenue?: number;
  /** Median age of active listings, in hours — what "freshness" means to a
      reader. Was hours-since-last-run until MPPI-00146, which read 3.9 while
      the typical listing was 27h old. */
  data_freshness: number;
  /** 90th percentile age. The tail is the part that matters. */
  data_freshness_p90: number;
  /** Hours since the last completed run: pipeline liveness, not data age. */
  last_ingestion_run_hours: number;
  active_trials: number;
  churn_rate: number;
}

export interface ActivityItem {
  id: string;
  action: string;
  actor: string;
  target: string;
  timestamp: string;
}

export function useDashboardKPIs() {
  return useQuery({
    queryKey: queryKeys.dashboard.kpis(),
    queryFn: () => adminFetch<DashboardKPIs>("/api/v1/admin/dashboard/kpis"),
    refetchInterval: 30_000,
  });
}

export function useDashboardActivity() {
  return useQuery({
    queryKey: queryKeys.dashboard.activity(),
    queryFn: () => adminFetch<ActivityItem[]>("/api/v1/admin/dashboard/activity"),
    refetchInterval: 30_000,
  });
}

export function useDashboardFreshness() {
  return useQuery({
    queryKey: queryKeys.dashboard.freshness(),
    queryFn: () =>
      adminFetch<Record<string, { status: string; last_updated: string }>>(
        "/api/v1/admin/dashboard/freshness",
      ),
    refetchInterval: 30_000,
  });
}

export function useDashboardHealth() {
  return useQuery({
    queryKey: queryKeys.dashboard.health(),
    queryFn: () =>
      adminFetch<Record<string, { status: string; latency_ms: number }>>(
        "/api/v1/admin/dashboard/health",
      ),
    refetchInterval: 30_000,
  });
}

export interface RevenueDataPoint {
  month: string;
  mrr: number;
  arr: number;
  /** True for the month still in progress — a partial bucket that must not be
   *  drawn like a complete one (MPPI-00108). */
  is_partial?: boolean;
}

export function useDashboardRevenue() {
  return useQuery({
    queryKey: queryKeys.dashboard.revenue(),
    queryFn: () => adminFetch<RevenueDataPoint[]>("/api/v1/admin/dashboard/revenue"),
    refetchInterval: 60_000,
  });
}

export interface FunnelStage {
  stage: string;
  count: number;
}

export function useDashboardFunnel() {
  return useQuery({
    queryKey: queryKeys.dashboard.funnel(),
    queryFn: () => adminFetch<FunnelStage[]>("/api/v1/admin/dashboard/funnel"),
    refetchInterval: 60_000,
  });
}

export interface UserGrowthPoint {
  date: string;
  total_users: number;
  new_registrations: number;
}

export function useDashboardUserGrowth() {
  return useQuery({
    queryKey: queryKeys.dashboard.userGrowth(),
    queryFn: () => adminFetch<UserGrowthPoint[]>("/api/v1/admin/dashboard/user-growth"),
    refetchInterval: 60_000,
  });
}

export interface SocialPipelineSource {
  platform: string;
  status: string;
  records_today: number;
  last_run: string | null;
  error_count: number;
}

export function useDashboardSocialPipeline() {
  return useQuery({
    queryKey: queryKeys.dashboard.socialPipeline(),
    queryFn: () =>
      adminFetch<SocialPipelineSource[]>("/api/v1/admin/dashboard/social-pipeline"),
    refetchInterval: 30_000,
  });
}

export interface LeadKPIs {
  total_prospects: number;
  new_leads_7d: number;
  avg_lead_score: number;
  qualified_leads: number;
}

export function useDashboardLeadKPIs() {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "lead-kpis"] as const,
    queryFn: () => adminFetch<LeadKPIs>("/api/v1/admin/dashboard/lead-kpis"),
    refetchInterval: 60_000,
  });
}

export interface InventoryBreakdownRow {
  label: string;
  secondary: number;
  developer_primary: number;
  off_plan: number;
  rental: number;
  for_sale_total: number;
  /** Community rows only: dominant emirate + DM district for the subtitle. */
  emirate?: string | null;
  district?: string | null;
}

export interface InventorySummary {
  properties: {
    total: number;
    new_7d: number;
    off_plan: number;
    /** Off-plan pipeline, classified exactly as the By Developer chart does
     *  so the two cannot disagree (MPPI-00157). */
    offplan_projects: { in_progress: number; planned: number; total: number };
    /** Available (on-market) inventory — active listing + residential only. */
    available_total?: number;
    available_secondary?: number;
    available_completed_primary?: number;
    available_off_plan?: number;
    avg_deal_score: number | null;
    total_listings: number;
    total_projects: number;
    distinct_sources: number;
    /** F-022 — properties with no active+unstaged listing. Preserved
     *  for historic pricing / AVM comparable inputs. */
    expired_properties?: number;
    by_type: { type: string; count: number }[];
    top_communities: { community: string; count: number }[];
    /** Stacked breakdown per type / community, ordered by for-sale total. */
    by_type_breakdown?: InventoryBreakdownRow[];
    by_community_breakdown?: InventoryBreakdownRow[];
  };
  /** Rental entity counters (deduplicated for-rent units). */
  rentals?: {
    total: number;
    new_7d: number;
    below_market: number;
    avg_implied_yield: number | null;
  };
  vehicles: {
    total: number;
    new_7d: number;
  };
}

export function useDashboardInventory() {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "inventory"] as const,
    queryFn: () =>
      adminFetch<InventorySummary>("/api/v1/admin/dashboard/inventory-summary"),
    refetchInterval: 60_000,
  });
}

// --- Market intelligence panels ---

export interface PriceDrop {
  listing_id: string;
  property_id: string | null;
  source: string;
  title: string | null;
  source_url: string | null;
  community: string | null;
  sub_community: string | null;
  emirate: string | null;
  bedrooms: number | null;
  floor_area_sqft: number | null;
  property_type: string | null;
  current_price_aed: number | null;
  original_price_aed: number | null;
  change_pct: number | null;
  last_price_change_date: string | null;
}

export function useDashboardPriceDrops(days = 1, limit = 15) {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "price-drops", days, limit] as const,
    queryFn: () =>
      adminFetch<PriceDrop[]>(
        `/api/v1/admin/dashboard/price-drops?days=${days}&limit=${limit}`,
      ),
    refetchInterval: 60_000,
  });
}

export interface DistressedSignal {
  community: string | null;
  emirate: string | null;
  high: number;
  medium: number;
  total: number;
}

export function useDashboardDistressedSignals(limit = 12) {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "distressed", limit] as const,
    queryFn: () =>
      adminFetch<DistressedSignal[]>(
        `/api/v1/admin/dashboard/distressed-signals?limit=${limit}`,
      ),
    refetchInterval: 60_000,
  });
}

export interface LeadFunnelBySource {
  source: string;
  new: number;
  contacted: number;
  qualified: number;
  negotiating: number;
  converted: number;
  rejected: number;
  total: number;
}

export function useDashboardLeadFunnelBySource() {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "lead-funnel-by-source"] as const,
    queryFn: () =>
      adminFetch<LeadFunnelBySource[]>(
        "/api/v1/admin/dashboard/lead-funnel-by-source",
      ),
    refetchInterval: 60_000,
  });
}

export interface GeoActivityPoint {
  listing_id: string;
  lat: number | null;
  lon: number | null;
  community: string | null;
  emirate: string | null;
  property_type: string | null;
  bedrooms: number | null;
  listing_price_aed: number | null;
  price_per_sqft_aed: number | null;
  created_at: string | null;
}

export interface VehiclePriceDrop {
  vehicle_id: string;
  source: string;
  source_url: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  primary_image_url: string | null;
  emirate: string | null;
  current_price_aed: number | null;
  first_seen_price_aed: number | null;
  change_pct: number | null;
  last_price_change: string | null;
}

export function useDashboardVehiclePriceDrops(days = 14, limit = 12) {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "vehicle-price-drops", days, limit] as const,
    queryFn: () =>
      adminFetch<VehiclePriceDrop[]>(
        `/api/v1/admin/dashboard/vehicle-price-drops?days=${days}&limit=${limit}`,
      ),
    refetchInterval: 60_000,
  });
}

export function useDashboardGeoActivity(days = 7, limit = 500) {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "geo-activity", days, limit] as const,
    queryFn: () =>
      adminFetch<GeoActivityPoint[]>(
        `/api/v1/admin/dashboard/geo-activity?days=${days}&limit=${limit}`,
      ),
    refetchInterval: 120_000,
  });
}

export type HeatmapMatrix =
  | "beds_x_area"
  | "beds_x_price"
  | "community_x_beds"
  | "community_x_price"
  | "community_x_dom"
  | "type_x_emirate";

/** What a cell shows. `count` is the panel's original behaviour. */
export type HeatmapMetric =
  | "count"
  | "median_ppsf"
  | "median_deal_score"
  | "median_dom"
  | "pct_price_cut"
  | "median_gross_yield";

/** How to FORMAT a value — a median AED/sqft and a percentage are both
 *  numbers and must not be rendered the same way. */
export type HeatmapValueKind = "count" | "aed_sqft" | "score" | "days" | "pct";

/** Which end of the ramp is favourable to a buyer. `neutral` is used where
 *  asserting a direction would claim something the number does not say. */
export type HeatmapDirection = "high_good" | "high_bad" | "neutral";

export interface HeatmapResponse {
  matrix: HeatmapMatrix;
  metric: HeatmapMetric;
  metric_label: string;
  value_kind: HeatmapValueKind;
  direction: HeatmapDirection;
  rows: string[];
  cols: string[];
  row_label: string;
  col_label: string;
  /** Each tuple: [colIndex, rowIndex, value] — already shaped for ECharts.
   *  Cells whose sample is under `min_sample` are ABSENT, not zero. */
  data: [number, number, number][];
  /** [colIndex, rowIndex, properties] for every populated cell, including
   *  the ones withheld from `data`. A median has to be read beside the
   *  number of properties it came from. */
  sample: [number, number, number][];
  min_sample: number;
  cells_withheld: number;
}

export interface HeatmapFilters {
  emirate?: string | null;
  community?: string | null;
}

export function useDashboardHeatmap(
  matrix: HeatmapMatrix,
  metric: HeatmapMetric = "count",
  filters: HeatmapFilters = {},
) {
  const params = new URLSearchParams();
  params.set("metric", metric);
  if (filters.emirate) params.set("emirate", filters.emirate);
  if (filters.community) params.set("community", filters.community);
  const qs = params.toString();
  return useQuery({
    queryKey: [
      ...queryKeys.dashboard.all,
      "heatmap",
      matrix,
      metric,
      filters.emirate ?? "",
      filters.community ?? "",
    ] as const,
    queryFn: () =>
      adminFetch<HeatmapResponse>(
        `/api/v1/admin/dashboard/heatmap/${matrix}?${qs}`,
      ),
    refetchInterval: 5 * 60_000,
    // Keeps the current chart on screen while a filter change loads, rather
    // than blanking the panel on every selection.
    placeholderData: (prev) => prev,
  });
}

export interface HeatmapOptions {
  matrices: {
    value: HeatmapMatrix;
    label: string;
    row_label: string;
    col_label: string;
  }[];
  metrics: {
    value: HeatmapMetric;
    label: string;
    kind: HeatmapValueKind;
    direction: HeatmapDirection;
  }[];
  min_sample: number;
}

/** The matrices and metrics the endpoint supports.
 *
 *  Fetched rather than hardcoded so the drop-downs and the endpoint cannot
 *  disagree about which combinations exist. */
export function useHeatmapOptions() {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "heatmap-options"] as const,
    staleTime: 30 * 60_000,
    queryFn: () =>
      adminFetch<HeatmapOptions>("/api/v1/admin/dashboard/heatmap/options"),
  });
}

export interface HeatmapFilterOptions {
  emirates: string[];
  communities: { name: string; emirate: string; count: number }[];
}

/** Filter options. Communities come from the registry, so the dropdown is
 *  free of the scraper spill present in the raw column. */
export function useHeatmapFilterOptions() {
  return useQuery({
    queryKey: [...queryKeys.dashboard.all, "heatmap-filters"] as const,
    staleTime: 30 * 60_000,
    queryFn: () =>
      adminFetch<HeatmapFilterOptions>(
        "/api/v1/admin/dashboard/heatmap/filters",
      ),
  });
}

export type PropertyWatchKind = "sale" | "rent";
export type PropertyWatchSort = "added" | "score" | "gap" | "price";

export interface PropertyWatchRow {
  property_id: string;
  community: string | null;
  emirate: string | null;
  property_type: string | null;
  bedrooms: number | null;
  price_aed: number | null;
  deal_score: number | null;
  avm_aed: number | null;
  /** Percent BELOW valuation, so positive means asking under the AVM.
   *  Null when the property has no AVM — a strong Deal Score is still worth
   *  showing, and inventing a gap would be worse than an empty cell. */
  avm_gap_pct: number | null;
  /** When we first ingested the advert. `listing_date` is NULL across the
   *  whole table, so this is the only honest "date added" available. */
  added_at: string | null;
}

/** Paginated envelope (MPPI-00083). `total` is counted after duplicate
 *  collapsing, so the page count it implies is real. */
export interface PropertyWatchPage {
  items: PropertyWatchRow[];
  total: number;
  limit: number;
  offset: number;
}

export function usePropertyWatch(
  kind: PropertyWatchKind,
  sort: PropertyWatchSort = "added",
  days = 14,
  limit = 10,
  offset = 0,
) {
  return useQuery<PropertyWatchPage>({
    queryKey: [
      ...queryKeys.dashboard.all,
      "watch",
      kind,
      sort,
      days,
      limit,
      offset,
    ] as const,
    queryFn: () =>
      adminFetch<PropertyWatchPage>(
        `/api/v1/admin/dashboard/property-watch?kind=${kind}&sort=${sort}` +
          `&days=${days}&limit=${limit}&offset=${offset}`,
      ),
    refetchInterval: 120_000,
    // Hold the previous page while the next loads. Without it the table
    // collapses to the loader on every page step, so the panel jumps and the
    // dashboard reflows under the pointer.
    placeholderData: (prev) => prev,
  });
}

export interface TickerItem {
  key: string;
  label: string;
  value: number;
  /** Preformatted server-side so units and precision stay consistent. */
  display: string;
  as_of: string | null;
}

export function useTicker() {
  return useQuery<TickerItem[]>({
    queryKey: [...queryKeys.dashboard.all, "ticker"] as const,
    queryFn: () => adminFetch<TickerItem[]>("/api/v1/admin/dashboard/ticker"),
    refetchInterval: 300_000,
  });
}


/** One point on a Geo Activity layer (MPPI-00188). */
export interface GeoLayerPoint {
  lat: number;
  lon: number;
  community: string | null;
  property_type: string | null;
  bedrooms: number | null;
  area_sqft: number | null;
  /** Asking price, or ANNUAL RENT on the rental layer. Shared key, and the
   *  tooltip labels which it is — never rendered bare. */
  price: number | null;
  ppsf: number | null;
  id: string;
}

export interface GeoLayer {
  total: number;
  shown: number;
  capped: boolean;
  points: GeoLayerPoint[];
}

export type GeoLayerName =
  | "off_plan"
  | "developer"
  | "ready"
  | "secondary"
  | "rental";

export function useDashboardGeoLayers(limitPerSegment = 900) {
  return useQuery<{ segments: Record<GeoLayerName, GeoLayer> }>({
    queryKey: [...queryKeys.dashboard.all, "geo-layers", limitPerSegment] as const,
    // Inventory shifts on the scale of a scraper run, not a page view.
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      adminFetch(
        `/api/v1/admin/dashboard/geo-activity-layers?limit_per_segment=${limitPerSegment}`,
      ),
  });
}
